import { css } from "@emotion/react";
import { FC, MouseEventHandler } from "react";
import { useSelector } from "react-redux";

import { Button, DesignToken, Loader as LoaderComponent, Text } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import Role from "constants/role";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useOrganization } from "hooks/use-organizations";
import { useResendInvitation } from "hooks/useResendInvitation";
import { IAgent, selectLoggedInAgentPermission } from "store/entities/agents";
import { withVar } from "themes/helpers";

const Loader: FC = () => (
  <Flex width="80px" height="20px" justifyContent="center" alignContent="center">
    <LoaderComponent size="small" />
  </Flex>
);

interface IProps {
  agent: IAgent;
}

export const InvitationStatus: FC<IProps> = ({ agent }) => {
  const { resendInvitation, resendingInvitation, invitationReminderSent } = useResendInvitation();
  const { organization, isLoading: fetchingOrganization } = useOrganization();

  const role = useSelector(selectLoggedInAgentPermission);
  const isLoggedUserAdmin = role === Role.Owner;
  const isLoggedUserAgent = role === Role.Normal;

  const onlyAdminsCanInvite = organization?.invitationMode === "strict";
  const isInvitedUserAdmin = agent.permission === "owner";
  const canSendInvitationReminder =
    isLoggedUserAdmin || (isLoggedUserAgent && !onlyAdminsCanInvite && !isInvitedUserAdmin);

  const sendInvitationReminder: MouseEventHandler = e => {
    e.stopPropagation();
    trackEngagement("Agents: invitation reminder clicked", EventPlace.Agents, {
      role,
    });
    resendInvitation(agent.id);
  };

  if (agent.status !== "invited") return null;

  if (fetchingOrganization) {
    return <Loader />;
  }

  return (
    <Flex column>
      <Text size="sm" customColor={withVar(DesignToken.ContentBasicPrimary)}>
        Invitation sent
      </Text>
      {canSendInvitationReminder && (
        <>
          {resendingInvitation ? (
            <Loader />
          ) : (
            <>
              {invitationReminderSent ? (
                <Text
                  data-testid="reminder-sent-label"
                  css={css`
                    color: ${withVar(DesignToken.ContentBasicSecondary)};
                    line-height: 20px;
                  `}
                >
                  Reminder sent
                </Text>
              ) : (
                <Button
                  data-testid="send-invitation-reminder"
                  onClick={sendInvitationReminder}
                  kind="link"
                  size="compact"
                  css={css`
                    width: fit-content;
                  `}
                >
                  Send a reminder
                </Button>
              )}
            </>
          )}
        </>
      )}
    </Flex>
  );
};
